import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components/macro';
import { compose } from 'redux';
import { useHistory, useParams } from 'react-router';
import { useQuery } from 'react-query';
import { find, isEmpty, filter } from 'lodash';
import api from 'config/api';
import requester from 'utils/requester';
import scaleSize from 'utils/scale-size';
import { CONTENT_TYPES } from 'constants/content-types';
import PageTitle from 'components/PageTitle';
import CustomCollection from 'components/CustomCollection';
import WithMouseControls from 'components/HOCs/WithMouseControls';
import ScrollTopHack from 'components/ScrollTopHack';
import { useDispatch, useSelector } from 'react-redux';
import { appSelectors } from 'store/app';
import { appActions } from 'store/app';

export const StyledTitle = styled(PageTitle)`
  top: ${scaleSize(55)}px;
  left: ${scaleSize(220)}px;
  font-family: 'RobotoMedium', sans-serif;
`;

const apiCall = async (id) => {
  const { data } = await requester({
    method: 'get',
    url: `${api.host}${api.endpoints.screens}`,
    params: {
      $expand:
        'rails(preview(thumbnail_url,entitlement,bookmark,content_n)),carousel(picture_urls)',
      'ext_id[eq]': id,
    },
  });

  return data;
};

const ScreenPage = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const isHomePage = ['/', '/screen/home'].includes(history.location.pathname);

  const menuItems = useSelector(appSelectors.selectMenuItems);
  const currentMenuItem = useMemo(() => find(menuItems, { path: history.location.pathname }), [
    history.location.pathname,
    menuItems,
  ]);

  const { isLoading, data } = useQuery(['screen', id], () => apiCall(id));

  const onContentEnter = useCallback(
    (item) => {
      const itemType = item.asset_type || item.type;
      const itemID = item.asset_id || item.id;
      switch (itemType) {
        case 'movie':
        case 'series':
          // todo: case 'series':
          history.push(`/details/${itemType}/${itemID}`);
          break;
        case 'channel':
          history.push(`/player/channel/${itemID}`);
          break;
        default:
          // banner has no type
          dispatch(appActions.resolveBannerNavigation(item));
      }
    },
    [dispatch, history],
  );

  const collections = useMemo(() => {
    if (isEmpty(data)) return [];

    let banners = [];
    let lists = [];

    if (!isEmpty(data[0].carousel)) {
      banners = [
        {
          data: data[0].carousel.filter((banner) => banner.url.indexOf('http') === -1),
          contentType: CONTENT_TYPES.BANNER,
          id: 'banner',
        },
      ];
    }

    if (!isEmpty(data[0].rails)) {
      const fullLists = filter(data[0].rails, 'preview');
      lists = fullLists?.map((rail) => {
        return {
          data: rail.preview,
          contentType:
            rail.preview[0]?.asset_type === 'channel'
              ? CONTENT_TYPES.CHANNEL
              : CONTENT_TYPES.VOD_ASSET,
          title: rail.name,
          id: rail.id,
        };
      });
    }

    return [...banners, ...lists];
  }, [data]);

  return (
    <CustomCollection
      title={(!isHomePage && currentMenuItem?.title) || ''}
      showClock={isHomePage}
      showLogo={isHomePage}
      isLoading={isLoading}
      collections={collections}
      onContentEnter={onContentEnter}
      top={scaleSize(140)}
      left={scaleSize(220)}
      startListScrollIndex={1}
    />
  );
};

export default compose<typeof ScreenPage>(WithMouseControls, ScrollTopHack)(ScreenPage);
